import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Re-renders its own children whenever a variant is selected.
 *
 * Horizon only refreshes a handful of its own elements on a variant change.
 * Anything else — including a section further down the page — keeps whatever
 * it rendered for the first variant, which is how the cross-sell ended up
 * showing "Also in Warm Oak" after the shopper had picked walnut, and linking
 * to the wrong variant underneath.
 *
 * The variant change already fetches the whole product page, so the fresh
 * markup for this element is sitting in the event payload. We just find our
 * counterpart in it and morph the children across.
 *
 * Two details that matter:
 *
 * - The listener goes on the PRODUCT INFORMATION section, not on this
 *   element's own section. productSelect is dispatched there, and a section
 *   lower down the page never sees it otherwise.
 *
 * - childrenOnly, so this element's own attributes survive. Anything that has
 *   to change on a variant switch must therefore live INSIDE it, never on it.
 */
class AgVariantAware extends Component {
  #source = null;

  connectedCallback() {
    super.connectedCallback();
    this.#source = document.querySelector('[id*="ProductInformation-"]') ?? document;
    this.#source.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#source?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        // Match by key so several of these can coexist on one page.
        const key = this.dataset.agKey;
        const selector = key ? `ag-variant-aware[data-ag-key="${key}"]` : 'ag-variant-aware';
        const fresh = detail.html.querySelector(selector);
        if (!fresh) return;

        morph(this, fresh, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ag-variant-aware] rejected:', error);
      });
  };
}

if (!customElements.get('ag-variant-aware')) {
  customElements.define('ag-variant-aware', AgVariantAware);
}
